import { useEffect, useState } from "react";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useGetSystemPlaybooks } from "./api/useGetSystemPlaybooks";
import { useDeleteSystemPlaybook } from "./api/useDeleteSystemPlaybook";
import EditPlaybookModal from "./components/EditPlaybookModal";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Star } from "lucide-react";
import { useAdminUpdateFavoriteSystemPlaybook } from "./api/useAdminUpdateFavoriteSystemPlaybook";

export default function AdminPlaybooks() {
  const { systemPlaybooks, getSystemPlaybooks } = useGetSystemPlaybooks();
  const { isDeleted, deletePlaybook } = useDeleteSystemPlaybook();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<string>();
  const { updateFavoriteSystemPlaybook, isUpdatingFavorite } =
    useAdminUpdateFavoriteSystemPlaybook();

  useEffect(() => {
    getSystemPlaybooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditClick = (playbookId: string) => {
    setSelectedPlaybookId(playbookId);
    setIsModalOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedPlaybookId(undefined);
    setIsModalOpen(true);
  };

  const handleEditDelete = (playbookId: string) => {
    if (window.confirm("Are you sure you want to delete this playbook?")) {
      deletePlaybook(playbookId);
    }
  };

  useEffect(() => {
    if (!isDeleted) return;
    getSystemPlaybooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleted]);

  useEffect(() => {
    if (isUpdatingFavorite) return;
    getSystemPlaybooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatingFavorite]);

  const onClose = () => {
    getSystemPlaybooks();
    setIsModalOpen(false);
  };

  const handleFavoritePlaybook = (
    playbookId: string,
    isFavorite: boolean,
    playbookName: string,
  ) => {
    if (
      isFavorite &&
      !window.confirm(
        `Are you sure you want to set the playbook: "${playbookName}" as favorite?\n\n` +
          `This will set this playbook as favorite for all users.\n\nThis can not be undone!\n\n` +
          `If you later decide to remove this playbook from favorites, it will only affect new users, current users will still have it as favorite.\n\nPlease confirm.`,
      )
    ) {
      return;
    }
    updateFavoriteSystemPlaybook(playbookId, isFavorite);
  };

  return (
    <>
      <div className="p-4">
        <Button onClick={handleCreateClick} className="mb-4">
          Create Playbook
        </Button>
        {!systemPlaybooks ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-[90%]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Favorite</TableHead>
                    <TableHead>Edit</TableHead>
                    <TableHead>Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {systemPlaybooks.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.title}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`${p.isFavorite ? "text-yellow-400" : "text-gray-400"} hover:text-yellow-500`}
                          onClick={() => {
                            handleFavoritePlaybook(
                              p.id,
                              !p.isFavorite,
                              p.title,
                            );
                          }}
                        >
                          <Star
                            className="h-4 w-4"
                            fill={p.isFavorite ? "currentColor" : "none"}
                          />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditClick(p.id)}
                        >
                          <IconEdit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditDelete(p.id)}
                        >
                          <IconTrash className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
      <EditPlaybookModal
        opened={isModalOpen}
        close={onClose}
        playbookId={selectedPlaybookId}
      />
    </>
  );
}
